import { proxy } from 'valtio';

/**
 * Brand identifier for analytics and branding purposes
 */
export type Brand = 'camp67' | 'kumbh-sukrit';

/**
 * Analytics event names
 */
export enum AnalyticsEvent {
  BUTTON_CLICK = 'button_click',
  OPERATION_FAIL = 'operation_fail',
  OPERATION_SUCCESS = 'operation_success',
  PAGE_VIEW = 'page_view',
}

/**
 * Button tags for analytics tracking
 */
export enum ButtonTag {
  ADD_ACCOMMODATION = 'add_accommodation',
  SUBTRACT_ACCOMMODATION = 'subtract_accommodation',
  NEXT_STEP = 'next_step',
  PREV_STEP = 'prev_step',
  NEXT_ROOM = 'next_room',
  PREV_ROOM = 'prev_room',
  NEXT_IMAGE = 'next_image',
  PREV_IMAGE = 'prev_image',
  SUBMIT_RESERVATION = 'submit_reservation',
  ACCEPT_TANDC = 'accept_tandc',
  SELECT_RAZORPAY = 'select_razorpay',
  SELECT_PAYPAL = 'select_paypal',
  SELECT_PACKAGE = 'select_package',
}

/**
 * Button source for analytics context
 */
export enum ButtonSource {
  STEP_1 = 'step_1',
  STEP_2 = 'step_2',
  STEP_3 = 'step_3',
  COMPLETE_PAYMENT = 'complete_payment',
}

/**
 * Operation names for tracking success/failure
 */
export enum Operation {
  RESERVATION = 'reservation',
  PAYMENT = 'payment',
}

/**
 * Analytics provider interface (e.g., Amplitude)
 */
export interface AnalyticsProvider {
  track: (event: string, data?: Record<string, unknown>) => void;
  identify?: (userId: string, traits?: Record<string, unknown>) => void;
  setUserId?: (userId: string) => void;
}

/**
 * Analytics state - initialized by consumer with their analytics provider
 */
export const analyticsState = proxy<{
  provider: AnalyticsProvider | null;
  brand: Brand | null;
}>({
  provider: null,
  brand: null,
});

/**
 * Initialize analytics with a provider and brand
 */
export function initializeAnalytics(provider: AnalyticsProvider, brand: Brand) {
  analyticsState.provider = provider;
  analyticsState.brand = brand;
}

/**
 * Track an analytics event
 */
export function trackEvent(
  event: AnalyticsEvent | string,
  data?: Record<string, unknown>,
) {
  analyticsState.provider?.track(event, {
    ...data,
    brand: analyticsState.brand,
  });
}

/**
 * Track a button click
 */
export function trackButtonClick(
  buttonTag: ButtonTag,
  additionalData?: Record<string, unknown>,
) {
  trackEvent(AnalyticsEvent.BUTTON_CLICK, {
    buttonTag,
    ...additionalData,
  });
}
